import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit, Copy, Package, Store, Settings as Cog, Eye, EyeOff, Search } from "lucide-react";

const ADULT = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const KIDS = ["2-3 Years", "4-5 Years", "6-7 Years", "8-9 Years", "10-11 Years", "12-13 Years"];
const ORDER_STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = { pending: "#9ca3af", confirmed: "#1E88E5", packed: "#f59e0b", shipped: "#8b5cf6", delivered: "#16a34a", cancelled: "#FF3B30" };

export default function AdminShopPanel() {
  const [sub, setSub] = useState("products");
  const SUBS = [["products", "Products", Store], ["orders", "Orders", Package], ["config", "Config", Cog]];
  return (
    <div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {SUBS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`shopadmin-sub-${k}`} onClick={() => setSub(k)}
            className={`inline-flex items-center gap-1 px-4 py-2 border-2 border-black font-black uppercase text-xs ${sub === k ? "bg-black text-white" : "bg-white"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      {sub === "products" && <ProductsSub />}
      {sub === "orders" && <OrdersSub />}
      {sub === "config" && <ConfigSub />}
    </div>
  );
}

/* ---------------- Products ---------------- */
function ProductsSub() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/shop/products").then((r) => setProducts(r.data)).catch(() => toast.error("Failed to load products"));
    api.get("/admin/shop/config").then((r) => setCats(r.data.categories || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.category || "").toLowerCase().includes(q.toLowerCase()));

  const toggle = async (p) => { await api.patch(`/admin/shop/products/${p.id}/toggle`); load(); };
  const duplicate = async (p) => { await api.post(`/admin/shop/products/${p.id}/duplicate`); toast.success("Duplicated"); load(); };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return; await api.delete(`/admin/shop/products/${p.id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-black uppercase">Products ({products.length})</h2>
          <p className="text-sm text-neutral-500">Manage merchandise, inventory, variants & pricing.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-neutral-400" />
            <input data-testid="shopadmin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="brutal-input pl-8 text-sm" />
          </div>
          <button data-testid="shopadmin-add-product" onClick={() => setCreating(true)} className="brutal-btn flex items-center gap-1"><Plus size={16} /> Add Product</button>
        </div>
      </div>

      <div className="border-2 border-black overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black text-white">
            <tr>
              {["Image", "Name", "Category", "Price", "Stock", "Flags", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-black uppercase text-xs">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-neutral-500">No products. Click "Add Product" to create one.</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`shopadmin-row-${p.id}`} className="border-t-2 border-black align-middle">
                <td className="px-3 py-2"><img src={p.images?.[0]} alt="" className="w-12 h-12 object-cover border-2 border-black" /></td>
                <td className="px-3 py-2 font-bold max-w-[220px]">{p.name}{p.is_kids && <span className="ml-1 text-[10px] bg-pink-200 border border-black px-1">KIDS</span>}</td>
                <td className="px-3 py-2">{p.category}</td>
                <td className="px-3 py-2 font-black">₹{p.price_after_discount}{p.discount_pct > 0 && <span className="block text-[10px] text-neutral-400 line-through">₹{p.price}</span>}</td>
                <td className="px-3 py-2">
                  <span className={p.available_qty <= 5 ? "text-red-600 font-black" : "font-bold"}>{p.available_qty}</span>
                  <span className="text-[10px] text-neutral-400 block">of {(p.stock?.total ?? 0)} · sold {(p.stock?.sold ?? 0)}</span>
                </td>
                <td className="px-3 py-2 text-[10px] space-x-1">
                  {p.featured && <span className="bg-yellow-200 border border-black px-1">FEAT</span>}
                  {p.is_new && <span className="bg-green-200 border border-black px-1">NEW</span>}
                  {p.limited_edition && <span className="bg-red-200 border border-black px-1">LTD</span>}
                </td>
                <td className="px-3 py-2">
                  <button data-testid={`shopadmin-toggle-${p.id}`} onClick={() => toggle(p)} className={`inline-flex items-center gap-1 text-xs font-black uppercase px-2 py-1 border-2 border-black ${p.enabled ? "bg-green-200" : "bg-neutral-200"}`}>
                    {p.enabled ? <Eye size={12} /> : <EyeOff size={12} />}{p.enabled ? "Live" : "Hidden"}
                  </button>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button data-testid={`shopadmin-edit-${p.id}`} onClick={() => setEditing(p)} className="p-1.5 border-2 border-black mr-1 hover:bg-black hover:text-white" title="Edit"><Edit size={13} /></button>
                  <button data-testid={`shopadmin-dup-${p.id}`} onClick={() => duplicate(p)} className="p-1.5 border-2 border-black mr-1 hover:bg-black hover:text-white" title="Duplicate"><Copy size={13} /></button>
                  <button data-testid={`shopadmin-del-${p.id}`} onClick={() => del(p)} className="p-1.5 border-2 border-black hover:bg-red-600 hover:text-white" title="Delete"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && <ProductDialog product={editing} cats={cats} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />}
    </div>
  );
}

function ProductDialog({ product, cats, onClose, onSaved }) {
  const init = product || {};
  const [f, setF] = useState({
    name: init.name || "", category: init.category || (cats[0] || ""), description: init.description || "",
    imagesText: (init.images || []).join("\n"),
    colorsText: (init.colors || []).join(", "),
    adult_sizes: init.adult_sizes || [], kids_sizes: init.kids_sizes || [],
    price: init.price ?? "", discount_pct: init.discount_pct ?? 0,
    stock_total: init.stock?.total ?? 0,
    has_kids: !!init.has_kids, is_kids: !!init.is_kids,
    enabled: init.enabled !== false, featured: !!init.featured, is_new: !!init.is_new, limited_edition: !!init.limited_edition,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, val) => setF((s) => ({ ...s, [k]: s[k].includes(val) ? s[k].filter((x) => x !== val) : [...s[k], val] }));

  const save = async () => {
    if (!f.name.trim() || !f.category.trim()) { toast.error("Name and category are required"); return; }
    setBusy(true);
    const body = {
      name: f.name, category: f.category, description: f.description,
      images: f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      colors: f.colorsText.split(",").map((s) => s.trim()).filter(Boolean),
      adult_sizes: f.adult_sizes, kids_sizes: f.kids_sizes,
      price: parseFloat(f.price) || 0, discount_pct: parseFloat(f.discount_pct) || 0,
      stock_total: parseInt(f.stock_total) || 0,
      has_kids: f.has_kids, is_kids: f.is_kids,
      enabled: f.enabled, featured: f.featured, is_new: f.is_new, limited_edition: f.limited_edition,
    };
    try {
      if (product) await api.put(`/admin/shop/products/${product.id}`, body);
      else await api.post("/admin/shop/products", body);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-4 border-black rounded-none">
        <DialogHeader><DialogTitle className="font-black uppercase">{product ? "Edit Product" : "New Product"}</DialogTitle>
          <DialogDescription className="text-xs text-neutral-500">Set product details, pricing, inventory and variants.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-black uppercase">Name *</label>
            <input data-testid="prod-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="brutal-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-black uppercase">Category *</label>
              <input data-testid="prod-category" list="cat-list" value={f.category} onChange={(e) => set("category", e.target.value)} className="brutal-input" />
              <datalist id="cat-list">{cats.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-black uppercase">Price ₹</label><input data-testid="prod-price" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="brutal-input" /></div>
              <div><label className="text-xs font-black uppercase">Disc %</label><input data-testid="prod-discount" type="number" value={f.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} className="brutal-input" /></div>
            </div>
          </div>
          <div>
            <label className="text-xs font-black uppercase">Description</label>
            <textarea data-testid="prod-desc" value={f.description} onChange={(e) => set("description", e.target.value)} className="brutal-input" rows={2} />
          </div>
          <div>
            <label className="text-xs font-black uppercase">Image URLs (one per line)</label>
            <textarea data-testid="prod-images" value={f.imagesText} onChange={(e) => set("imagesText", e.target.value)} className="brutal-input" rows={2} placeholder="https://…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-black uppercase">Colors (comma-separated)</label><input data-testid="prod-colors" value={f.colorsText} onChange={(e) => set("colorsText", e.target.value)} className="brutal-input" placeholder="Black, White, Blue" /></div>
            <div><label className="text-xs font-black uppercase">Stock (total units)</label><input data-testid="prod-stock" type="number" value={f.stock_total} onChange={(e) => set("stock_total", e.target.value)} className="brutal-input" /></div>
          </div>
          <div>
            <label className="text-xs font-black uppercase">Adult Sizes</label>
            <div className="flex flex-wrap gap-1 mt-1">{ADULT.map((s) => <button key={s} type="button" onClick={() => toggleArr("adult_sizes", s)} className={`px-2 py-1 border-2 border-black text-xs font-black ${f.adult_sizes.includes(s) ? "bg-black text-white" : "bg-white"}`}>{s}</button>)}</div>
          </div>
          <div>
            <label className="text-xs font-black uppercase">Kids Sizes</label>
            <div className="flex flex-wrap gap-1 mt-1">{KIDS.map((s) => <button key={s} type="button" onClick={() => toggleArr("kids_sizes", s)} className={`px-2 py-1 border-2 border-black text-xs font-black ${f.kids_sizes.includes(s) ? "bg-black text-white" : "bg-white"}`}>{s}</button>)}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t-2 border-black">
            {[["enabled", "Live / Enabled"], ["featured", "Featured"], ["is_new", "New Arrival"], ["limited_edition", "Limited Edition"], ["has_kids", "Also has Kids sizes"], ["is_kids", "Kids-only product"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm font-bold"><Switch data-testid={`prod-flag-${k}`} checked={f[k]} onCheckedChange={(v) => set(k, v)} /> {label}</label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="brutal-btn-secondary">Cancel</button>
          <button data-testid="prod-save" disabled={busy} onClick={save} className="brutal-btn">{busy ? "Saving…" : "Save Product"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Orders ---------------- */
function OrdersSub() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => api.get("/admin/shop/orders").then((r) => setOrders(r.data)).catch(() => toast.error("Failed to load orders")), []);
  useEffect(() => { load(); }, [load]);

  const setOrderStatus = async (oid, s) => { try { await api.patch(`/admin/shop/orders/${oid}/status`, { status: s }); toast.success(`Marked ${s}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const filtered = orders.filter((o) => !status || o.status === status);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-black uppercase">Orders ({orders.length})</h2>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setStatus("")} className={`px-2 py-1 border-2 border-black text-xs font-black uppercase ${!status ? "bg-black text-white" : "bg-white"}`}>All</button>
          {ORDER_STATUSES.map((s) => <button key={s} data-testid={`shopadmin-order-filter-${s}`} onClick={() => setStatus(s)} className={`px-2 py-1 border-2 border-black text-xs font-black uppercase ${status === s ? "bg-black text-white" : "bg-white"}`}>{s}</button>)}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="border-2 border-black p-10 text-center text-neutral-500"><Package size={40} className="mx-auto mb-2" />No orders.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <div key={o.id} data-testid={`shopadmin-order-${o.order_number}`} className="border-2 border-black p-4">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <div>
                  <span className="font-black">{o.order_number}</span>
                  <span className="text-xs text-neutral-500 ml-2">{o.user_name} ({o.user_employee_id}) · {new Date(o.created_at).toLocaleString()}</span>
                </div>
                <span className="text-xs font-black uppercase px-2 py-1 border-2 border-black text-white" style={{ background: STATUS_COLORS[o.status] || "#666" }}>{o.status}</span>
              </div>
              <div className="text-sm space-y-0.5 mb-2">
                {o.lines.map((l, i) => <div key={i} className="flex justify-between"><span>{l.name} × {l.qty} {[l.color, l.adult_size, l.kids_size].filter(Boolean).length ? `(${[l.color, l.adult_size, l.kids_size].filter(Boolean).join(", ")})` : ""}</span><span>₹{l.line_total}</span></div>)}
              </div>
              <div className="text-xs text-neutral-600 border-t-2 border-black pt-2">
                Paid <b>₹{o.amount_paid}</b>{o.points_used > 0 && <> · Redeemed <b>{o.points_used} pts</b></>} · {o.payment_method?.toUpperCase()} · Ship to {o.address?.recipient_name}, {o.address?.city} {o.address?.pincode} · Est. {o.estimated_delivery}
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-xs font-black uppercase self-center mr-1">Set status:</span>
                {ORDER_STATUSES.map((s) => <button key={s} data-testid={`shopadmin-setstatus-${o.order_number}-${s}`} disabled={o.status === s} onClick={() => setOrderStatus(o.id, s)} className={`px-2 py-1 border-2 border-black text-xs font-black uppercase ${o.status === s ? "opacity-40" : "hover:bg-black hover:text-white"}`}>{s}</button>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Config ---------------- */
function ConfigSub() {
  const [cfg, setCfg] = useState(null);
  const [catsText, setCatsText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/shop/config").then((r) => { setCfg(r.data); setCatsText((r.data.categories || []).join("\n")); }).catch(() => {}); }, []);
  if (!cfg) return <div className="text-neutral-500">Loading…</div>;

  const toggleComingSoon = async (v) => {
    const prev = cfg.coming_soon;
    setCfg((c) => ({ ...c, coming_soon: v }));  // optimistic
    try {
      const r = await api.put("/admin/shop/config", { coming_soon: v });
      setCfg(r.data);
      setCatsText((r.data.categories || []).join("\n"));
      toast.success(v ? "Shop set to Coming Soon" : "Shop is now LIVE for employees");
    } catch (e) {
      setCfg((c) => ({ ...c, coming_soon: prev }));  // revert on failure
      toast.error(e?.response?.data?.detail || "Couldn't update — try again");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/shop/config", {
        pts_per_rupee: parseInt(cfg.pts_per_rupee) || 10,
        min_redeem_points: parseInt(cfg.min_redeem_points) || 0,
        cooldown_days: parseInt(cfg.cooldown_days) || 0,
        coming_soon: !!cfg.coming_soon,
        categories: catsText.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      setCfg(r.data); setCatsText((r.data.categories || []).join("\n"));
      toast.success("Shop config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <h2 className="text-xl font-black uppercase">Shop Configuration</h2>
      <div className="border-2 border-black p-4 flex items-center justify-between" style={{ background: cfg.coming_soon ? "#fef3c7" : "#dcfce7" }}>
        <div>
          <div className="font-black uppercase text-sm flex items-center gap-2"><Store size={16} /> "Coming Soon" Mode</div>
          <p className="text-xs text-neutral-600 mt-1">{cfg.coming_soon ? "Employees see a 'Coming Soon' screen. Turn OFF to open the shop with all products, cart & checkout." : "Shop is LIVE — employees can browse products, add to cart and checkout."} This applies instantly (no need to Save Config).</p>
        </div>
        <Switch data-testid="cfg-coming-soon" checked={!!cfg.coming_soon} onCheckedChange={toggleComingSoon} />
      </div>
      <div className="border-2 border-black p-4 space-y-3">
        <div className="font-black uppercase text-sm">Points Redemption</div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-xs font-black uppercase">Points per ₹1</label><input data-testid="cfg-pts-rate" type="number" value={cfg.pts_per_rupee} onChange={(e) => setCfg({ ...cfg, pts_per_rupee: e.target.value })} className="brutal-input" /></div>
          <div><label className="text-xs font-black uppercase">Min points to redeem</label><input data-testid="cfg-min-redeem" type="number" value={cfg.min_redeem_points} onChange={(e) => setCfg({ ...cfg, min_redeem_points: e.target.value })} className="brutal-input" /></div>
          <div><label className="text-xs font-black uppercase">Cooldown (days)</label><input data-testid="cfg-cooldown" type="number" value={cfg.cooldown_days} onChange={(e) => setCfg({ ...cfg, cooldown_days: e.target.value })} className="brutal-input" /></div>
        </div>
        <p className="text-xs text-neutral-500">Employees earn game points and can redeem them for rupee value. E.g. {cfg.pts_per_rupee} points = ₹1.</p>
      </div>
      <div className="border-2 border-black p-4 space-y-2">
        <div className="font-black uppercase text-sm">Categories (one per line)</div>
        <textarea data-testid="cfg-categories" value={catsText} onChange={(e) => setCatsText(e.target.value)} className="brutal-input" rows={8} />
      </div>
      <button data-testid="cfg-save" disabled={busy} onClick={save} className="brutal-btn">{busy ? "Saving…" : "Save Config"}</button>
    </div>
  );
}
